import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { PositionsTable } from '@Components/Tables/PositionsTable';
import { BILLING_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { cashRegisterColumns, ledgerAccountColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useTseClient } from '@Hooks/Billing';
import { useBadges } from '@Hooks/useBadges';
import {
    type ApiCashJournal,
    type ApiCashJournalPosition,
    type ApiCashRegister,
    type ApiLedgerAccount,
    EnumFrontendLayoutSetting,
    EnumProcessState,
    isApproved,
} from '@Model';
import { FrameCashJournalPosition, FrameEditCashRegister } from '@Modules/Billing';
import { FrameEditLedgerAccount } from '@Modules/Stammdaten';
import {
    Button,
    Card,
    Column,
    ConfirmationModal,
    DateInput,
    EditFrameMode,
    EditFrameShell,
    EnumMessageLevel,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    type Index,
    isLinked,
    NumberInput,
    TextInput,
    UnlabeledField,
    useEditSubObject,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedField,
    useLinkProperty,
    useModalState,
    useProp,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { TestTseButton } from '../TestTseButton';

function forceReadOnly(cash_journal: ApiCashJournal, name: Index<ApiCashJournal>) {
    if (isApproved(cash_journal.process_state)) {
        return name !== 'remarks';
    } else {
        return false;
    }
}

export function FrameEditCashJournal(props: IFrameEditShellProps<ApiCashJournal>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiCashJournal>
            key={primaryKey}
            primaryKey={primaryKey}
            include={['positions']}
            embedded={props.embedded || props.primaryKey !== undefined}
            apiResourcePath={`${BILLING_API}/cash_journals`}
            renderer={FrameEditCashJournalBody}
            canEdit={props.canEdit !== false && hasPermission(Permission.BillingCashJournalsUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.BillingCashJournalsCreate)}
            canDelete={false}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

const getPositionColumns = (
    hasTse: boolean,
    isSignaturePending: boolean,
    triggerSignature: (position: ApiCashJournalPosition) => void,
) => {
    const columns: Column<ApiCashJournalPosition>[] = [
        {
            name: 'number',
            label: 'Nummer',
        },
        {
            name: 'price_gross',
            label: 'Brutto-Preis in €',
            type: 'currency',
        },
        {
            name: 'tse_signature',
            label: 'TSE Signatur',
            disableColumnFilter: true,
            disableColumnSort: true,
            renderCell: ({ rowData }) => {
                const isSignatureLinked: boolean = isLinked(rowData.tse_signature);

                const label = isSignatureLinked
                    ? 'Signiert'
                    : isSignaturePending
                      ? 'Wird signiert'
                      : hasTse
                        ? 'Jetzt signieren'
                        : 'Keine Kasse vorhanden';
                const buttonType =
                    isSignatureLinked || !hasTse ? 'transparent' : isSignaturePending ? 'outlined' : 'filled';

                return (
                    <Button
                        onClick={() => triggerSignature(rowData)}
                        label="SignPositionButton"
                        locked={!hasTse || isSignaturePending || isSignatureLinked}
                        appearance={buttonType}
                    >
                        {label}
                    </Button>
                );
            },
        },
    ];
    return columns;
};

function FrameEditCashJournalBody(props: IFrameBodyProps<ApiCashJournal>) {
    const i18n = useTranslate('');

    const requestUpdateState = props.requestUpdateState;
    const onSaveEvent = props.events.onSave;
    const stateQueueDepth = props.stateQueueDepth;

    const apiCashJournal = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;

    const cashJournalBadges = useBadges(apiCashJournal);

    const isNotInViewMode = props.mode !== EditFrameMode.View;
    const canEditPositions = isNotInViewMode && !forceReadOnly(apiCashJournal, 'positions');

    const cashJournalCanBeApproved = !isApproved(apiCashJournal.process_state);

    const hasCashRegister = isLinked(apiCashJournal.cash_register);
    const tseClient = useTseClient(hasCashRegister ? apiCashJournal.cash_register.foreign_key ?? '' : '');
    const hasTse = hasCashRegister && tseClient.isTseAvailable;

    const binder = useGuiBinder<ApiCashJournal>({
        ...props,
        canEdit: isNotInViewMode,
        forceReadOnly: (name: string) => forceReadOnly(apiCashJournal, name as Index<ApiCashJournal>),
    });

    const { isOpen: isApprovalModalOpen, open: openApprovalModal, close: closeApprovalModal } = useModalState();

    const { linkApi: ledgerAccountApi, events: ledgerAccountOverlayEvents } = useLinkedField<
        ApiCashJournal,
        ApiLedgerAccount
    >(baseApiObjectPath, 'ledger_account', FrameEditLedgerAccount, true, props.requestUpdateState);

    const { linkApi: cashRegisterApi, events: cashJournalOverlayEvents } = useLinkedField<
        ApiCashJournal,
        ApiCashRegister
    >(baseApiObjectPath, 'cash_register', FrameEditCashRegister, true, props.requestUpdateState);

    const dateField = useProp(binder, 'date');
    const ledgerAccountField = useLinkProperty(binder, ledgerAccountApi);
    const cashRegisterField = useLinkProperty(binder, cashRegisterApi);
    const remarksField = useProp(binder, 'remarks');

    const {
        addSubObject: addPositionHandler,
        deleteSubObjects: deletePositionsHandler,
        requestUpdateSubObject: requestUpdatePosition,
    } = useEditSubObject<ApiCashJournalPosition, ApiCashJournal>(
        baseApiObjectPath,
        'positions',
        props.requestUpdateState,
    );

    const triggerSignature = useCallback(
        (position: ApiCashJournalPosition) =>
            requestUpdatePosition(
                (latest, saveData) =>
                    saveData({
                        ...latest,
                        process_state: EnumProcessState.Approved,
                    }),
                position.id,
            ),
        [requestUpdatePosition],
    );

    const positionColumns = useMemo(
        () => getPositionColumns(hasTse, stateQueueDepth > 0, triggerSignature),
        [hasTse, stateQueueDepth, triggerSignature],
    );

    const approve = useCallback(async () => {
        closeApprovalModal();

        if (onSaveEvent) {
            const saveResult = await onSaveEvent();
            if (!saveResult) {
                return false;
            }
        }

        return requestUpdateState((latest, saveData) =>
            saveData({
                ...latest,
                process_state: EnumProcessState.Approved,
            }),
        );
    }, [onSaveEvent, closeApprovalModal, requestUpdateState]);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Kassenbuch', props.mode),
                resourceLabel: 'Kassenbuch',
                ...props.events,
                actionItems: [
                    cashJournalCanBeApproved && (
                        <Button key="approve" appearance="filled" onClick={openApprovalModal} variant="secondary">
                            Freigeben
                        </Button>
                    ),
                ],
            }}
            bannerProps={{
                mode: props.mode,
                loading: stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="maindata" border={'withoutBorder'} label="Allgemeine Informationen" badges={cashJournalBadges}>
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <DateInput type="day" {...dateField} label={i18n('Datum')} />
                        <NumberInput
                            name="day_value"
                            value={apiCashJournal.end_value - apiCashJournal.start_value}
                            onChange={() => false}
                            label={i18n('Tagesbilanz in €')}
                            decimals={2}
                            locked
                        />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <NumberInput
                            name="start_value"
                            value={apiCashJournal.start_value}
                            onChange={() => false}
                            label={i18n('Startsumme in €')}
                            decimals={2}
                            locked
                        />
                        <NumberInput
                            name="end_value"
                            value={apiCashJournal.end_value}
                            onChange={() => false}
                            label={i18n('Gesamtsumme in €')}
                            decimals={2}
                            locked
                        />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="cash_register">
                <Grid columns={12}>
                    <GridColumns spans={[4, 4, 4]}>
                        <LinkedFieldWithLayoutSettings<ApiLedgerAccount>
                            layoutSettingsContext={EnumFrontendLayoutSetting.LedgerAccountsTableLayout}
                            {...ledgerAccountField}
                            {...ledgerAccountOverlayEvents}
                            columns={ledgerAccountColumns}
                            label="Sachkonto"
                        />
                        <LinkedFieldWithLayoutSettings<ApiCashRegister>
                            layoutSettingsContext={EnumFrontendLayoutSetting.CashJournalsTableLayout}
                            {...cashRegisterField}
                            {...cashJournalOverlayEvents}
                            columns={cashRegisterColumns}
                            label="Kasse"
                        />
                        {hasCashRegister && (
                            <UnlabeledField>
                                <TestTseButton isTseAvailable={tseClient.isTseAvailable} onPing={tseClient.pingTse} />
                            </UnlabeledField>
                        )}
                    </GridColumns>
                    <GridColumn span={12}>
                        <TextInput {...remarksField} inputSize={3} label={'Bemerkung'} />
                    </GridColumn>
                </Grid>
            </Card>
            <Card name="positions" label={`${i18n('Positionen')} (${apiCashJournal.positions.length})`}>
                <PositionsTable<ApiCashJournalPosition>
                    layoutSettingsContext={EnumFrontendLayoutSetting.CashJournalPositionsTableLayout}
                    items={apiCashJournal.positions}
                    columns={positionColumns}
                    initialSorting={{
                        columnName: 'number',
                        direction: 'asc',
                    }}
                    name={'CashJournalPositions'}
                    editable={canEditPositions}
                    onAdd={addPositionHandler}
                    onDelete={deletePositionsHandler}
                    canCreate={canEditPositions}
                    renderSubComponent={(row) => (
                        <FrameCashJournalPosition
                            key={row.id}
                            data={row}
                            meta={undefined}
                            canEdit={canEditPositions}
                            apiResourcePath={`${baseApiObjectPath}/positions`}
                            processState={apiCashJournal.process_state}
                            requestUpdateState={(updater) => requestUpdatePosition(updater, row.id)}
                            stateQueueDepth={stateQueueDepth}
                            refetchData={props.refetchData}
                        />
                    )}
                />
            </Card>

            <ConfirmationModal
                title={'Wirklich freigeben?'}
                message={`Wollen Sie die das Kassenbuch wirklich freigeben?`}
                messageLevel={EnumMessageLevel.Information}
                isOpen={isApprovalModalOpen}
                onConfirm={approve}
                onCancel={closeApprovalModal}
            />
        </EditFrameShell>
    );
}
